/**
 * PatientHome — Today tab (iter111).
 *
 * Layout:
 *   ┌─ Non-movable header (grid columns: sticker / greeting / streak) ─┐
 *   │  [Sticker]  [Greeting: date, name, goal]              [Streak]   │
 *   │  [Quote spanning the same width as sticker+greeting]             │
 *   └─────────────────────────────────────────────────────────────────┘
 *   ┌─ SortableGrid (patient_home_v2) — 4-col on desktop, 2-col mobile ┐
 *   │  msg_clinician / goal / rewards / plan / adherence / booking /   │
 *   │  notifications / qa_* shortcuts — all adaptive (1×1, 2×1, 1×2,   │
 *   │  2×2) with real content at every size.                            │
 *   └─────────────────────────────────────────────────────────────────┘
 *   Below the grid: Daily Check-In (safety gate — kept outside the grid).
 *
 * Migration rule: on load we strip greeting/sticker/streak/quote out of any
 * saved v2 order so previously-customised layouts don't render phantom cards
 * inside the grid, but we DO NOT touch the positions or sizes of the
 * remaining cards. See `useMergedHomeLayout`.
 */
import { useEffect, useState } from "react";
import { mutate as swrMutate } from "swr";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Flame, ArrowRight, Sparkles, Trophy, CheckCircle2, Eye, EyeOff, RefreshCw, ClipboardList, CalendarClock, Dumbbell, Calendar } from "lucide-react";
import { toast } from "sonner";
import DailyQuote from "@/components/DailyQuote";
import StickerStudio from "@/components/StickerStudio";
import DailyCheckinFields from "@/components/DailyCheckinFields";
import AdaptiveWidget from "@/components/adaptiveWidgets";
import SortableGrid from "@/components/SortableGrid";
import { useMergedHomeLayout } from "@/hooks/useMergedHomeLayout";
import { useEditMode } from "@/lib/editMode";
import { Skeleton } from "@/components/Skeleton";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

/* ─────────────────────────── Fixed header ─────────────────────────── */
function StickerBox({ sticker, onOpenStudio }) {
  return (
    <button
      onClick={onOpenStudio}
      className="relative shrink-0 group focus:outline-none focus:ring-2 focus:ring-[#f47a20] rounded-xl"
      title="Personalise your athlete card"
      data-testid="header-sticker"
    >
      {sticker?.image_data_url ? (
        <img
          src={sticker.image_data_url}
          alt="Your sticker"
          className="w-20 h-20 md:w-24 md:h-24 rounded-xl object-cover transition-transform group-hover:scale-105"
          style={{ border: "2px solid var(--fl-orange)" }}
        />
      ) : (
        <div
          className="w-20 h-20 md:w-24 md:h-24 rounded-xl flex flex-col items-center justify-center transition-all group-hover:scale-105"
          style={{ border: "2px dashed var(--fl-border)", background: "var(--fl-tint-a)" }}
        >
          <Trophy size={20} className="text-[color:var(--fl-orange)]" />
          <span className="text-[9px] mt-1 fl-label">Make card</span>
        </div>
      )}
      <span
        className="absolute -bottom-1 -right-1 text-[9px] px-1.5 py-0.5 rounded font-bold uppercase"
        style={{ background: "var(--fl-orange)", color: "#0A0A0A" }}
      >
        {sticker ? "Edit" : "New"}
      </span>
    </button>
  );
}

function StreakBox({ streaks }) {
  if (!streaks) return <Skeleton className="w-28 h-28 rounded-xl" data-testid="header-streak-skeleton" />;
  const days = streaks.login?.current ?? 0;
  const completedToday = !!streaks.completed_today;
  return (
    <div
      className={
        "fl-card fl-card-elevated flex flex-col items-center justify-center gap-1 p-3 w-28 h-28 shrink-0 " +
        (completedToday ? "bg-gradient-to-br from-amber-500/25 to-amber-400/5" : "bg-transparent")
      }
      style={{
        border: completedToday ? "2px solid #F5B942" : "1px solid var(--fl-border)",
        boxShadow: completedToday ? "0 0 24px -6px rgba(245, 185, 66, 0.55)" : "none",
      }}
      data-testid="header-streak"
    >
      <div
        className="relative flex items-center justify-center w-10 h-10 rounded-lg"
        style={{ background: completedToday ? "rgba(245, 185, 66, 0.18)" : "rgba(254, 103, 20, 0.14)" }}
      >
        <Flame size={22} className={completedToday ? "text-amber-300" : "text-[#f47a20]"} />
        {completedToday && (
          <span className="absolute -bottom-1 -right-1 rounded-full bg-emerald-500 text-black w-4 h-4 flex items-center justify-center">
            <CheckCircle2 size={11} strokeWidth={3} />
          </span>
        )}
      </div>
      <div className="fl-label text-[9px] leading-none">{completedToday ? "Streak · ✓" : "Daily streak"}</div>
      <div className="flex items-baseline gap-1">
        <span className={"fl-heading text-2xl leading-none " + (completedToday ? "text-amber-300" : "text-[#f47a20]")} data-testid="header-streak-count">{days}</span>
        <span className="text-[10px] text-zinc-400">d</span>
      </div>
    </div>
  );
}

function GreetingBox({ user, t }) {
  return (
    <div className="flex-1 min-w-0" data-testid="header-greeting">
      <div className="fl-label text-[#f47a20]">
        Today · {new Date().toLocaleDateString("en-GB", { weekday: "long", month: "long", day: "numeric" })}
      </div>
      <h1 className="fl-heading text-3xl md:text-4xl mt-2 leading-tight truncate">
        {t("home.greetPrefix")} {user?.name?.split(" ")[0]}{t("home.greetSuffix")}
      </h1>
      <p className="text-zinc-400 mt-2 text-sm line-clamp-2">
        {user?.main_goal || t("home.defaultGoal")}
      </p>
    </div>
  );
}

function QuoteBox() {
  // Single container, no duplicated icon. DailyQuote renders the icon inline.
  return (
    <Card className="fl-card fl-card-elevated p-4 border-white/10 bg-transparent" data-testid="header-quote">
      <DailyQuote />
    </Card>
  );
}

/* ─────────────────────── Batch 8C · Next Action ─────────────────────── */
const NEXT_ACTION_ICON = {
  form: ClipboardList,
  review_due: CalendarClock,
  plan: Dumbbell,
  checkin: Sparkles,
  appointment: Calendar,
  up_to_date: CheckCircle2,
};

function NextActionCard({ data }) {
  if (!data) return null;
  const Icon = NEXT_ACTION_ICON[data.action] || Sparkles;
  const calm = data.action === "up_to_date";
  return (
    <div className="fl-card fl-card-elevated p-4 sm:p-5 mb-6" data-testid="next-action-card" data-action={data.action}>
      <div className="flex items-start gap-3 sm:gap-4 flex-wrap">
        <div className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
             style={{ background: calm ? "rgba(34,197,94,.14)" : "rgba(244,122,32,.14)" }}>
          <Icon size={20} className={calm ? "text-emerald-400" : "text-[#f47a20]"} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="fl-label text-[10px]" style={{ color: calm ? "#34d399" : "#f47a20" }}>Next action</div>
          <h2 className="fl-heading text-lg sm:text-xl mt-0.5" data-testid="next-action-title">{data.title}</h2>
          {data.detail && <p className="text-sm text-zinc-400 mt-1">{data.detail}</p>}
          {data.case_title && (
            <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                 style={{ background: "rgba(244,122,32,.14)", color: "#f8a766" }} data-testid="next-action-case">
              {data.case_title}
            </div>
          )}
        </div>
        {data.cta_link && data.cta_label && (
          <Link to={data.cta_link} className="fl-btn text-sm px-4 py-2.5 shrink-0" data-testid="next-action-cta">
            {data.cta_label} <ArrowRight size={14} />
          </Link>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────── Layout defaults ─────────────────────────── */
const HOME_V2_DEFAULT_ORDER = [
  "msg_clinician",
  "goal", "rewards",
  "plan", "adherence", "booking", "notifications",
  "qa_checkin", "qa_appointments", "qa_messages", "qa_videos", "qa_forms",
  "qa_progress", "qa_wellness", "qa_tests",
  "qa_learn", "qa_community", "qa_membership", "qa_wearables", "qa_security",
];
const HOME_V2_DEFAULT_SIZES = {
  msg_clinician: "2x1",
  plan: "2x1",
  goal: "1x1",
  rewards: "1x1",
};
// qa_* legacy ids map to canonical kinds one-to-one.
const kindFor = (id) => id.replace(/^qa_/, "");

/* ─────────────────────────── Page ─────────────────────────── */
export default function PatientHome() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [lastCheckin, setLast] = useState(null);
  // iter111.4 · Canonical daily-check-in shape (matches PatientCoach + the
  // /api/coach/checkin backend model). Sleep quality / energy / stress /
  // confidence are 1–5. Pain 0–10. Sleep hours in h. Comments + body map
  // are persisted with the same doc so both tabs render identical state.
  const [ci, setCi] = useState({
    pain: 3, sleep_hours: 7, sleep_quality: 3, energy: 3, stress: 3, confidence: 4,
    session_rpe: null, comments: "", pain_locations: [],
  });
  // iter111.6 · Per-field touch tracking. Fields NOT in the set will
  // be sourced from the saved coach doc (or omitted for first-time save)
  // on submit so a single slider adjustment never silently overwrites
  // other measurements.
  const [touchedFields, setTouchedFields] = useState(() => new Set());
  const [formTouched, setFormTouched] = useState(false);
  const [todaySaved, setTodaySaved] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sticker, setSticker]       = useState(null);
  const [showStudio, setShowStudio] = useState(false);
  const [streaks, setStreaks]       = useState(null);
  const [today, setToday] = useState(null);
  const [adh, setAdh] = useState({ adherence: null, has_schedule: null });
  const [nextAction, setNextAction] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());
  const [checkinHidden, setCheckinHidden] = useState(() => {
    try { return localStorage.getItem("fl_checkin_hidden") === "1"; }
    catch { return false; }
  });

  const { editing } = useEditMode();
  const {
    visibleOrder, setOrder, toggleHidden, setSize, sizes,
    hidden, resetToDefault,
  } = useMergedHomeLayout(HOME_V2_DEFAULT_ORDER, HOME_V2_DEFAULT_SIZES);

  const refresh = async () => {
    const [c, s, tod, na, adhr] = await Promise.all([
      api.get("/checkins/mine").catch(() => ({ data: [] })),
      api.get("/me/streaks").catch(() => ({ data: null })),
      api.get("/checkins/today").catch(() => ({ data: null })),
      api.get("/my-plan/next-action").catch(() => ({ data: null })),
      api.get("/stats/adherence").catch(() => ({ data: null })),
    ]);
    setLast((c.data || [])[0] || null);
    setStreaks(s.data);
    setToday(tod.data);
    setAdh(adhr.data || { adherence: null, has_schedule: null });
    setNextAction(na.data);
    // iter111.5 · The canonical saved doc for the summary + form prime
    // comes from the coach_checkin row (same row both entry points write).
    // We NEVER touch the daily "check-ins" mirror for state; that's only
    // used by the wellness adapter which merges both sources.
    const saved = tod.data?.coach || null;
    setTodaySaved(saved);
    if (saved) {
      setCi((prev) => ({
        ...prev,
        pain:           saved.pain           ?? prev.pain,
        sleep_hours:    saved.sleep_hours    ?? prev.sleep_hours,
        sleep_quality:  saved.sleep_quality  ?? prev.sleep_quality,
        energy:         saved.energy         ?? prev.energy,
        stress:         saved.stress         ?? prev.stress,
        confidence:     saved.confidence     ?? prev.confidence,
        session_rpe:    saved.session_rpe    ?? prev.session_rpe,
        // Never pre-fill the comment box with the saved value (which may be an
        // auto-generated parameter summary). The box stays empty so it only
        // ever reflects what the patient actually types. The saved comment is
        // still shown read-only in the "Today's check-in complete" card above.
        comments:       "",
        pain_locations: Array.isArray(saved.pain_locations) ? saved.pain_locations : (prev.pain_locations || []),
      }));
      setFormTouched(true); // saved values pre-populate — safe to submit updates
    }
  };
  const loadSticker = async () => {
    try { const { data } = await api.get("/patient/sticker/mine"); setSticker(data); } catch { /* silent */ }
  };
  useEffect(() => { refresh(); loadSticker(); }, []);
  useEffect(() => {
    if (!today?.next_allowed_at) return undefined;
    const timer = setInterval(() => setNowTs(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, [today?.next_allowed_at]);

  const nextAllowedAt = today?.next_allowed_at ? new Date(today.next_allowed_at) : null;
  const withinCoachLock = nextAllowedAt && nextAllowedAt.getTime() > nowTs;
  const lockCountdown = withinCoachLock
    ? (() => {
        const ms = nextAllowedAt.getTime() - nowTs;
        const h = Math.floor(ms / 3_600_000);
        const m = Math.floor((ms % 3_600_000) / 60_000);
        return `${h}h ${String(m).padStart(2, "0")}m`;
      })()
    : null;

  const submitCheckin = async () => {
    setSubmitting(true);
    try {
      // iter111.6 · Build the payload from the SAVED coach doc, then
      // overlay only the fields the user has actually touched. This
      // guarantees adjusting one slider never overwrites the other
      // seven with pristine defaults.
      const src = { ...(todaySaved || {}) };
      const build = (k) => (touchedFields.has(k) ? ci[k] : src[k] ?? ci[k]);
      const payload = {
        pain: build("pain"),
        sleep_hours: build("sleep_hours"),
        sleep_quality: build("sleep_quality"),
        energy: build("energy"),
        stress: build("stress"),
        confidence: build("confidence"),
        session_rpe: touchedFields.has("session_rpe") ? (ci.session_rpe === null ? null : Number(ci.session_rpe)) : (src.session_rpe ?? null),
        comments: touchedFields.has("comments") ? ((ci.comments || "").trim() || null) : (src.comments ?? null),
        pain_locations: touchedFields.has("pain_locations") ? (ci.pain_locations || []) : (src.pain_locations || []),
      };
      await api.post("/coach/checkin", payload);
      toast.success("Check-in saved — synced to Check-In & AI Coach");
      setTouchedFields(new Set());
      refresh();
      // Phase 1A · revalidate the shared Physical Wellness dataset so the
      // Today widget reflects the new/edited check-in without a reload.
      swrMutate("wellness:merged");
    } catch (err) {
      const status = err?.response?.status;
      const d = err?.response?.data?.detail;
      if (status === 409 && d?.next_allowed_at) {
        setToday((prev) => ({ ...(prev || {}), next_allowed_at: d.next_allowed_at }));
        toast.error(d.message || "You've already checked in recently.");
      } else {
        toast.error("Save failed — your draft is kept. Try again.");
      }
    } finally { setSubmitting(false); }
  };

  return (
    <DashLayout>
      {/* ─── Fixed non-movable header ─── */}
      {/* iter111 · Mobile lays out as: [sticker | streak] on row 1,
          greeting full-width on row 2, quote below. Desktop stays as
          [sticker | greeting | streak] with quote below. */}
      <div className="mb-6" data-testid="patient-home-header">
        <div className="flex flex-wrap items-start gap-4">
          <StickerBox sticker={sticker} onOpenStudio={() => setShowStudio(true)} />
          <div className="hidden md:block flex-1 min-w-0"><GreetingBox user={user} t={t} /></div>
          <StreakBox streaks={streaks} />
          <div className="w-full md:hidden"><GreetingBox user={user} t={t} /></div>
        </div>
        <div className="mt-4">
          <QuoteBox />
        </div>
      </div>

      {/* ─── Batch 8C · Next Action (one clear priority) ─── */}
      <NextActionCard data={nextAction} />

      {/* ─── Grid controls ─── */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="fl-label text-xs text-zinc-500">Your customisable widgets</div>
        <div className="flex items-center gap-2 flex-wrap">
          {editing && (
            <button type="button" onClick={resetToDefault} className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white" data-testid="reset-layout-home">
              Reset layout
            </button>
          )}
          {editing && hidden.size > 0 && (
            <div className="flex items-center gap-1 text-[10px] text-zinc-400" data-testid="hidden-chips-home">
              <span className="uppercase tracking-widest">Hidden:</span>
              {[...hidden].map((id) => (
                <button key={id} type="button" onClick={() => toggleHidden(id)} className="px-2 py-0.5 rounded border border-white/10 hover:border-[color:var(--fl-orange)]" data-testid={`unhide-home-${id}`}>+ {id}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ─── 6-hour AI Coach lock banner ─── */}
      {withinCoachLock && (
        <div className="fl-card p-3 mb-4 flex items-center gap-2 text-xs" style={{ borderColor: "color-mix(in oklab, var(--fl-orange) 40%, transparent)", color: "var(--fl-muted)" }} data-testid="patient-home-coach-lock">
          <Sparkles size={14} className="text-[color:var(--fl-orange)] shrink-0" />
          <span>You can still edit today's check-in — the AI Coach will next react in <b style={{ color: "var(--fl-orange)" }}>{lockCountdown}</b>.</span>
        </div>
      )}

      {/* ─── Adaptive Sortable Grid ─── */}
      <SortableGrid
        items={visibleOrder}
        onReorder={setOrder}
        onHide={toggleHidden}
        onResize={setSize}
        sizes={sizes}
        editMode={editing}
        className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
      >
        {visibleOrder.map((id) => (
          <SortableGrid.Item key={id} id={id}>
            <AdaptiveWidget kind={kindFor(id)} testid={`widget-${id}`} />
          </SortableGrid.Item>
        ))}
      </SortableGrid>

      {/* ─── Daily Check-In (safety gate — outside the grid) ─── */}
      {(() => {
        // iter111.5 · Summary + form both read from the same canonical
        // coach_checkin doc. Labels + scales below match the shared
        // DailyCheckinFields contract exactly.
        const saved = todaySaved;
        const doneToday = !!saved;
        return (
          <Card className="fl-card p-0 mb-10 border-white/10 bg-transparent" data-testid="checkin-card">
            <CardHeader className="p-6 pb-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="fl-heading text-2xl">{t("home.checkinTitle")}</CardTitle>
                  <CardDescription className="text-sm text-zinc-400 mt-1">{t("home.checkinSub")}</CardDescription>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {lastCheckin && <div className="text-xs text-zinc-500">{t("home.lastCheckin")}: {new Date(lastCheckin.created_at).toLocaleString("en-GB")}</div>}
                  <button type="button" onClick={() => setCheckinHidden((v) => { const n = !v; try { localStorage.setItem("fl_checkin_hidden", n ? "1" : "0"); } catch { /* noop */ } return n; })} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-widest transition" style={{ color: "#f47a20", background: "transparent", border: "1px solid rgba(244, 122, 32, 0.28)", boxShadow: "0 2px 12px -6px rgba(0, 0, 0, 0.45)" }} data-testid="checkin-hide-toggle" aria-pressed={checkinHidden}>
                    {checkinHidden ? <><Eye size={12} /> Show</> : <><EyeOff size={12} /> Hide</>}
                  </button>
                </div>
              </div>
            </CardHeader>

            {!checkinHidden && (
            <CardContent className="p-6 pt-0">
              {doneToday ? (
                <div className="fl-card fl-card-elevated p-4 mb-5 border-l-4 border-l-emerald-500" data-testid="ci-today-summary">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="fl-label text-emerald-400">Today's check-in complete</div>
                      <div className="text-xs text-zinc-500 mt-0.5">Submitted at {new Date(saved.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                    <Link to="/app/coach" className="text-xs text-[#f47a20] hover:underline" data-testid="ci-open-coach">Open detailed check-in →</Link>
                  </div>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-4" data-testid="ci-today-values">
                    {[
                      ["Pain",          saved.pain,           10, "/10"],
                      ["Sleep",         saved.sleep_hours,    16, "h"],
                      ["Sleep quality", saved.sleep_quality,  5,  "/5"],
                      ["Energy",        saved.energy,         5,  "/5"],
                      ["Stress",        saved.stress,         5,  "/5"],
                      ["Confidence",    saved.confidence,     5,  "/5"],
                    ].map(([label, val, max, unit]) => (
                      <div key={label}>
                        <div className="fl-label">{label}</div>
                        <div className="fl-heading text-lg mt-1 text-[#f47a20]">{val == null ? "—" : val}<span className="text-xs text-zinc-500 ml-1">{unit}</span></div>
                        <div className="h-1 bg-white/10 rounded mt-1 overflow-hidden"><div className="h-full bg-[#f47a20]" style={{ width: `${((val || 0) / max) * 100}%` }} /></div>
                      </div>
                    ))}
                  </div>
                  {saved.comments && (
                    <div className="mt-3 text-xs text-zinc-400" data-testid="ci-today-comments">
                      <span className="fl-label text-[10px] block mb-0.5">Comment</span>
                      <div className="line-clamp-3">{saved.comments}</div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="fl-card p-4 mb-5 border-l-4 border-l-[#f47a20]" data-testid="ci-prompt">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="fl-label text-[#f47a20]">Daily reminder</div>
                      <div className="text-sm mt-1">You haven't checked in today — take a minute to log how you're feeling so your clinician can adjust your plan.</div>
                    </div>
                    <Link to="/app/coach" className="fl-btn text-xs fl-cta-pulse" data-testid="ci-open-detailed">Open detailed check-in <ArrowRight size={12} /></Link>
                  </div>
                </div>
              )}

              <DailyCheckinFields
                value={ci}
                onChange={(next) => { setCi(next); setFormTouched(true); }}
                onFieldTouch={(f) => setTouchedFields((prev) => { const n = new Set(prev); n.add(f); return n; })}
                touchedFields={touchedFields}
                adherence={adh.adherence}
                hasSchedule={adh.has_schedule}
                disabled={withinCoachLock}
              />

              {!formTouched && !todaySaved && (
                <div className="mt-4 text-xs" style={{ color: "var(--fl-muted)" }} data-testid="ci-untouched-hint">
                  Adjust the sliders above to record today's numbers — a comment alone won't save default values as measurements.
                </div>
              )}

              <Button onClick={submitCheckin} disabled={submitting || withinCoachLock || (!formTouched && !todaySaved)} className="mt-4 bg-[#f47a20] hover:bg-[#d96812] text-black font-semibold disabled:opacity-60" data-testid="ci-submit">
                {withinCoachLock ? <>✓ Checked in · unlocks in {lockCountdown}</> : (submitting ? t("home.checkinSaving") : t("home.checkinSave"))}
                {!withinCoachLock && <ArrowRight size={14} className="ml-1.5" />}
              </Button>
            </CardContent>
            )}
          </Card>
        );
      })()}

      {showStudio && <StickerStudio onClose={() => { setShowStudio(false); loadSticker(); }} />}
    </DashLayout>
  );
}
